export type PrimeAccent = "mint" | "peach" | "butter";

export type PrimeCapability = {
  title: string;
  body: string;
  proof: string;
};

export type PrimeMetric = { value: string; label: string };

export type PrimeProcessStep = { n: string; title: string; body: string };

export type PrimeFaqItem = { q: string; a: string };

export type PrimeSignatureWork = {
  title: string;
  client: string;
  role: string;
  year: string;
  platform: string;
  caseSlug?: string;
  /** Optional YouTube video id for embedded playback in the signature reel. */
  videoId?: string;
  /** Optional Spotify show URL (https://open.spotify.com/show/...) for embedded player. */
  spotifyShowUrl?: string;
};

export type PrimeHero = {
  /** Optional looped reel — when empty we render a tasteful gradient poster. */
  videoSrc?: string;
  /** Aspect for the hero media frame: cinematic, widescreen or square. */
  aspect: "anamorphic" | "wide" | "portrait" | "square";
  /** Visual treatment hint for the hero — drives subtle per-discipline differences. */
  flavor: "cinema" | "broadcast" | "studio" | "director" | "post" | "podcast";
};

export type PrimeService = {
  slug: string;
  number: string;
  title: string;
  shortTitle: string;
  intro: string;
  metaTitle: string;
  metaDescription: string;
  accent: PrimeAccent;
  hero: PrimeHero;
  /** One-liner that sits in the cinematic hero overlay. */
  kicker: string;
  signature: PrimeSignatureWork;
  capabilities: PrimeCapability[];
  proofLogos: string[];
  proofMetrics: PrimeMetric[];
  whenItFits: string[];
  notForYou: string[];
  deliverables: string[];
  priceFrom: { amount: string; unit: string; note: string };
  pricingPackages?: { name: string; price: string; unit: string; bullets: string[] }[];
  process: PrimeProcessStep[];
  faq: PrimeFaqItem[];
  related: string[];
  /** Optional production stills shown in a marquee on the service page. */
  stills?: string[];
  /** Optional YouTube videos shown in the Prime video reel section. */
  videos?: { videoId: string; title: string; client?: string }[];
};

export const primeServices: PrimeService[] = [
  {
    slug: "reklam-dokumentar",
    number: "01",
    shortTitle: "Reklam & dokumentär",
    title: "Reklam, dokumentär & storytelling",
    accent: "mint",
    kicker: "Filmiska kampanjer som rör, övertygar och stannar kvar.",
    intro:
      "Visuellt storslagna filmer med djup — riktade mot varumärkesbyggande. Reklamfilm, dokumentär och narrativ storytelling, alltid med regissörens öga för detalj.",
    metaTitle: "Reklamfilm & dokumentär | Prime — Fenny",
    metaDescription:
      "Vi producerar reklamfilm och dokumentärt berättande för varumärken som vill röra, övertyga och stanna kvar. Manus, regi, foto och post i samma team.",
    hero: { aspect: "anamorphic", flavor: "cinema" },
    signature: {
      title: "Brand film — kapitel 01",
      client: "Konfidentiell uppdragsgivare",
      role: "Koncept · Regi · Foto · Post",
      year: "2026",
      platform: "Bio · OLV · Social",
      caseSlug: undefined,
      videoId: "9iiyNS4yNzA",
    },
    capabilities: [
      {
        title: "Reklamfilm med själ",
        body: "Kampanjfilm, brand films och image-spots där koncept, regi och hantverk samspelar.",
        proof: "Anamorft ARRI-team · 6–10 cutdowns / hero",
      },
      {
        title: "Dokumentär & purpose-film",
        body: "Verkliga berättelser om människor, kultur eller hållbarhet — research, intervjuteknik och dramaturgi.",
        proof: "Egna researchers · etisk samtyckesprocess",
      },
      {
        title: "Narrativ storytelling",
        body: "Längre format som bygger position över tid: grundarberättelse, kunskaps­serier, mini-dokumentärer.",
        proof: "Säsongs­båge · återanvändbara cutdowns",
      },
    ],
    proofLogos: ["SVT", "TV4", "Volvo", "ICA", "H&M", "Spotify"],
    proofMetrics: [
      { value: "12M+", label: "kombinerade views senaste 12 mån" },
      { value: "94%", label: "completion rate på OLV-hero" },
      { value: "Guld", label: "Spinn 2025 — branded story" },
    ],
    whenItFits: [
      "Ny varumärkesplattform eller repositionering",
      "Stora kampanjlanseringar",
      "Företag med en stark berättelse som ännu inte fått filmiskt format",
    ],
    notForYou: [
      "Snabba sociala tester på löpande basis (välj Pulse)",
      "Rena förklaringsfilmer av en produktfunktion",
      "Projekt där priset väger tyngre än hantverket",
    ],
    deliverables: [
      "Hero-film (60–180 s) + 6–10 cutdowns",
      "Stillbilder från inspelning",
      "Format för biograf, OLV, broadcast och sociala",
    ],
    priceFrom: {
      amount: "Från 350 000 kr",
      unit: "/ produktion",
      note: "Indikativt — fullt team, hero + cutdowns. Pris sätts efter brief.",
    },
    process: [
      { n: "01", title: "Brief & vision", body: "Workshop med marketing och ledning. Vad ska filmen göra på riktigt?" },
      { n: "02", title: "Treatment & lookbook", body: "Manus, regi-intention, referensbank och produktionsplan." },
      { n: "03", title: "Förproduktion", body: "Casting, scouting, ljus- och riggplanering, godkända storyboards." },
      { n: "04", title: "Inspelning", body: "Regissör, foto, ljud och ljus. Hela teamet på plats — inga lösa trådar." },
      { n: "05", title: "Post & leverans", body: "Klipp, grading i Resolve, ljudmix, cutdowns till alla format." },
    ],
    faq: [
      {
        q: "Hur lång tid tar en hero-produktion?",
        a: "Räkna med 8–12 veckor från godkänt treatment till färdig hero, beroende på casting och plats. Vi kan komprimera vid behov.",
      },
      {
        q: "Äger vi materialet efteråt?",
        a: "Ja. Ni får råmaterial, masters och en grafikmapp som ni kan återanvända. Musik- och bildrättigheter specificeras alltid per projekt.",
      },
      {
        q: "Kan ni jobba med vår befintliga byrå?",
        a: "Absolut — vi kommer ofta in som filmpartner till en strategisk byrå och tar ansvar för regi, foto och post.",
      },
    ],
    related: ["regi-manus", "film-streaming-tv"],
    stills: [
      "/prime-stills/garageportexperten-fenny-1.jpg",
      "/prime-stills/fenny-reklam-film-1.jpg",
      "/prime-stills/bolt-fenny-1.jpg",
      "/prime-stills/garageportexperten-fenny-2.jpg",
      "/prime-stills/raaaabaatar-fenny-1.jpg",
      "/prime-stills/fenny-reklam-film-2.jpg",
      "/prime-stills/garageportexperten-fenny-3.jpg",
      "/prime-stills/bolt-fenny-2.jpg",
      "/prime-stills/wimeck-fenny-1.jpg",
      "/prime-stills/fenny-reklam-film-3.jpg",
      "/prime-stills/garageportexperten-fenny-4.jpg",
      "/prime-stills/raaaabaatar-fenny-2.jpg",
      "/prime-stills/bolt-fenny-3.jpg",
      "/prime-stills/fenny-reklam-film-4.jpg",
    ],
    videos: [
      { videoId: "O2zdqLfmM5E", title: "Garageportexperten Hemma", client: "Garageportexperten" },
      { videoId: "9iiyNS4yNzA", title: "Case – Maria på Garageportexperten", client: "Garageportexperten" },
      { videoId: "WONrMkeJuik", title: "Wimeck Detailing – Allt på en plats", client: "Wimeck" },
    ],
  },
  {
    slug: "film-streaming-tv",
    number: "02",
    shortTitle: "Film, streaming & TV",
    title: "Film, streaming & TV",
    accent: "peach",
    kicker: "Serier, trailers och långformat som tål en streamingplattform.",
    intro:
      "Serier, trailers och långformat för organisationer som söker filmisk nivå som tål att streamas. Premiumkvalitet, optimerat för plattformarna.",
    metaTitle: "Film, streaming & TV-produktion | Prime — Fenny",
    metaDescription:
      "Vi producerar serier, trailers och långformat för streaming och broadcast. Premiumkvalitet, plattformsanpassat och kommersiellt smart.",
    hero: { aspect: "wide", flavor: "broadcast" },
    
    signature: {
      title: "Branded series — säsong 01",
      client: "Konfidentiell uppdragsgivare",
      role: "Format · Regi · Produktion · Distribution",
      year: "2026",
      platform: "YouTube · Vimeo OTT · Broadcast",
      videoId: "HwWGVOl6dtg",
    },
    capabilities: [
      {
        title: "Serieformat",
        body: "Konceptutveckling, säsongsbåge och episodregi för branded series och fakta­serier.",
        proof: "Säsongsbibel · återkommande publik",
      },
      {
        title: "Trailers & teasers",
        body: "Korta format som säljer in en idé, ett event eller en lansering — byggda för retention.",
        proof: "60–90 s hero + sociala cutdowns",
      },
      {
        title: "Långformat & broadcast",
        body: "Filmer från 20 minuter och uppåt med leveransstandard för SVT, TV4, streamingplattformar.",
        proof: "EBU R128 · ProRes 422 HQ · QC",
      },
    ],
    proofLogos: ["SVT", "TV4", "Discovery+", "Viaplay", "Spotify", "Vimeo"],
    proofMetrics: [
      { value: "4K HDR", label: "leverans i broadcast-spec" },
      { value: "+38%", label: "session-tid på serier vs. enskilda filmer" },
      { value: "8 ep", label: "snittlängd på en branded season" },
    ],
    whenItFits: [
      "Branded content som ska bo på en streamingplattform",
      "Sponsorpaket kring TV- eller eventproduktioner",
      "Internprojekt med ambition att tävla med riktig TV",
    ],
    notForYou: [
      "Engångsfilmer under 60 s — välj Pulse eller reklam-spåret",
      "Innehåll utan en tydlig publik eller distributionsplan",
      "Projekt utan budget för broadcast-finish (mix, QC, mastrar)",
    ],
    deliverables: [
      "Episoder enligt format (10–45 min)",
      "Trailer, teaser och social-cutdowns",
      "Mastrar i broadcast-spec (EBU R128, ProRes 422 HQ)",
    ],
    priceFrom: {
      amount: "Från 180 000 kr",
      unit: "/ avsnitt",
      note: "Indikativt för branded series, exkl. talang-honorar och musik­rättigheter.",
    },
    process: [
      { n: "01", title: "Format & säsongsbåge", body: "Vinkel, programledare, gästflöde och säsongens röda tråd." },
      { n: "02", title: "Pilot & lookbook", body: "Visuellt språk, titelsekvens och en pilot som sätter tonen." },
      { n: "03", title: "Produktionsblock", body: "Vi spelar in flera avsnitt per block för effektivitet och kontinuitet." },
      { n: "04", title: "Post & broadcast-finish", body: "Klipp, grade, ljudmix enligt R128 och QC mot plattform." },
      { n: "05", title: "Distribution", body: "Leverans till plattform, trailers, sociala cutdowns och rapport." },
    ],
    faq: [
      {
        q: "Klarar ni broadcast-leveranser?",
        a: "Ja. Vi levererar i ProRes 422 HQ, EBU R128-mix och QC:ar mot specifik plattform (SVT, TV4, Discovery, Viaplay).",
      },
      {
        q: "Hjälper ni till med distribution?",
        a: "Vi paketerar avsnitt, trailers och cutdowns och kan koppla in våra plattformskontakter när det behövs.",
      },
      {
        q: "Kan ni göra en pilot först?",
        a: "Ofta gör vi det. En pilot låter oss bevisa formatet och säkra investerings­beslut för en hel säsong.",
      },
    ],
    related: ["reklam-dokumentar", "regi-manus"],
    stills: [
      "/prime-stills/recompany-1.jpg",
      "/prime-stills/fenny-reklam-film-5.jpg",
      "/prime-stills/bolt-fenny-4.jpg",
      "/prime-stills/recompany-2.jpg",
      "/prime-stills/raaaabaatar-fenny-3.jpg",
      "/prime-stills/fenny-reklam-film-6.jpg",
      "/prime-stills/bolt-fenny-5.jpg",
      "/prime-stills/fenny-reklam-film-7.jpg",
      "/prime-stills/raaaabaatar-fenny-4.jpg",
    ],
    videos: [
      { videoId: "HwWGVOl6dtg", title: "ALSO Huawei Energy Solution in Denmark", client: "ALSO / Huawei" },
      { videoId: "yvojXrb41Aw", title: "Bolt kampanj (20 sek)", client: "Bolt" },
      { videoId: "BzHML38R2bs", title: "Pricka — reklam", client: "Pricka" },
      { videoId: "WONrMkeJuik", title: "Wimeck Detailing – Allt på en plats", client: "Wimeck" },
    ],
  },
  {
    slug: "studio-live-webinars",
    number: "03",
    shortTitle: "Studio, live & webinars",
    title: "Studio, live & webinars",
    accent: "butter",
    kicker: "Sänd live med tv-känsla — utan teknikångest.",
    intro:
      "Format för livesändningar, webinarier och event. Konceptutveckling, visuell dramaturgi och varumärkesfokuserad leverans — i realtid.",
    metaTitle: "Live, studio & webinars | Prime — Fenny",
    metaDescription:
      "Vi producerar livesänd studio, webinarier och hybridevent. Flerkamera, regi, grafik och stream — i din studio eller vår.",
    hero: { aspect: "wide", flavor: "studio" },
    signature: {
      title: "Live talkshow — månadssändning",
      client: "Konfidentiell uppdragsgivare",
      role: "Format · Studio · Regi · Stream",
      year: "2026",
      platform: "YouTube · LinkedIn · Teams",
      videoId: "GPm8ubB79DM",
    },
    capabilities: [
      {
        title: "Multikamera-studio",
        body: "3–6 kameror, regi, vision-mixing, ljus och ljud. Talkshow-, panel- eller intervjuformat.",
        proof: "ATEM · vMix · NDI-pipeline",
      },
      {
        title: "Live-stream & hybridevent",
        body: "Distribution till YouTube, LinkedIn, Vimeo, Teams eller egen plattform med fallback och chat-moderering.",
        proof: "Dubblerad encoder · chat-mod",
      },
      {
        title: "Webinar-format",
        body: "Återanvändbara mallar med grafik, presentations­integration och färdiga klipp efteråt.",
        proof: "Lower thirds · grafik­mallar · klipp",
      },
    ],
    proofLogos: ["Telia", "EQT", "SEB", "Microsoft", "AstraZeneca", "H&M"],
    proofMetrics: [
      { value: "99,9%", label: "uptime på sändningar 2024–2025" },
      { value: "< 24h", label: "highlight-cutdowns levererade" },
      { value: "4K", label: "multikamera-produktion i realtid" },
    ],
    whenItFits: [
      "Återkommande sändningar (månadsvis talkshow, partner­event)",
      "Konferenser och stora interna kickoffs",
      "Produktlanseringar som behöver realtidsräckvidd",
    ],
    notForYou: [
      "Snabba interna Teams-möten — räcker med en bra mick",
      "Engångs-webinar med < 50 deltagare och låg budget",
      "Format utan en tydlig programledare eller dramaturgi",
    ],
    deliverables: [
      "Livesänd produktion i upp till 4K",
      "Inspelning för on-demand",
      "Highlight-cutdowns levererade samma vecka",
    ],
    priceFrom: {
      amount: "Från 95 000 kr",
      unit: "/ sändning",
      note: "Indikativt för 3-kamera studio inkl. regi, grafik och stream-paket.",
    },
    process: [
      { n: "01", title: "Format & rundown", body: "Vi sätter blocken, gästerna, grafiken och tidsplanen." },
      { n: "02", title: "Teknisk plan", body: "Kameror, ljus, ljud, encoder, fallback och chat-strategi." },
      { n: "03", title: "Rehearsal", body: "Genrep på riktigt — programledare och gäster vet exakt vad som händer." },
      { n: "04", title: "Live", body: "Vi regisserar, mixar bild och ljud och håller koll på chatten." },
      { n: "05", title: "Cutdowns", body: "Highlight-klipp för LinkedIn, IG och YouTube inom 24–48 h." },
    ],
    faq: [
      {
        q: "Måste vi ha en egen studio?",
        a: "Nej. Vi sänder från vår studio, er lokal eller bygger temporärt på event-plats — samma teknik och kvalitet.",
      },
      {
        q: "Vad händer om internet går ner?",
        a: "Vi kör dubblerad uppkoppling (fast + 4G/5G-bonding) och dubbel encoder. Sändningen ligger kvar även om en lina dör.",
      },
      {
        q: "Kan ni integrera vår presentation och vår chat?",
        a: "Ja — vi tar in slides, bilder och frågor från chatten i realtid och kan moderera publikfrågor.",
      },
    ],
    related: ["regi-manus", "post-produktion"],
    stills: [
      "/prime-stills/also-fenny-1.jpg",
      "/prime-stills/pricka-fenny-1.jpg",
      "/prime-stills/elektronikbranschen-fenny-1.jpg",
      "/prime-stills/also-fenny-2.jpg",
      "/prime-stills/pricka-fenny-2.jpg",
      "/prime-stills/elektronikbranschen-fenny-2.jpg",
      "/prime-stills/also-fenny-3.jpg",
      "/prime-stills/elektronikbranschen-fenny-3.jpg",
    ],
    videos: [
      { videoId: "GPm8ubB79DM", title: "Årets Pryl 2026", client: "Elektronikbranschen" },
      { videoId: "UU_Ys1M431I", title: "Årets Pryl 2026 — Motivering", client: "Elektronikbranschen" },
      { videoId: "HwWGVOl6dtg", title: "ALSO Huawei Energy Solution in Denmark", client: "ALSO / Huawei" },
    ],
  },
  {
    slug: "regi-manus",
    number: "04",
    shortTitle: "Regi, manus & direction",
    title: "Regi, manus & direction",
    accent: "mint",
    kicker: "Vi tar idén från servett till färdigt produktions­manus.",
    intro:
      "Vi regisserar idéer som förtjänar att glänsa. Från förfinat manus till tydlig vision som lyfter storytelling-potentialen till nästa nivå.",
    metaTitle: "Regi, manus & creative direction | Prime — Fenny",
    metaDescription:
      "Manusarbete, regi och creative direction för film, reklam och dokumentär. Vi tar visioner från idé till genomförbart koncept.",
    hero: { aspect: "portrait", flavor: "director" },
    signature: {
      title: "Treatment & regi — kampanj",
      client: "Konfidentiell uppdragsgivare",
      role: "Manus · Treatment · Regi on-set",
      year: "2026",
      platform: "Bio · OLV · Social",
      videoId: "O2zdqLfmM5E",
    },
    capabilities: [
      {
        title: "Manus & koncept",
        body: "Idéutveckling, treatment, dialog och voice-over. Skriven för det format ni faktiskt ska producera.",
        proof: "Treatment + shotlist + storyboard",
      },
      {
        title: "Regi & skådespelarledning",
        body: "Erfarna regissörer som leder talang, skådespelare och experter framför kameran.",
        proof: "Table-read · regi on-set",
      },
      {
        title: "Creative direction",
        body: "Visuell ton, art direction, look & feel och referensbank som håller hela kampanjen samman.",
        proof: "Lookbook · art bible · grade-LUT",
      },
    ],
    proofLogos: ["Volvo", "Spotify", "H&M", "SVT", "Polestar", "EQT"],
    proofMetrics: [
      { value: "20+", label: "regisserade kampanjer per år" },
      { value: "3", label: "regissörer i fast roster" },
      { value: "100%", label: "godkända treatments innan inspelning" },
    ],
    whenItFits: [
      "Ni har idén men behöver någon som tar den i mål",
      "Befintligt manus behöver skarpas till",
      "Större produktioner där ni vill ha en namngiven regissör",
    ],
    notForYou: [
      "Rena copy-uppdrag utan visuell vision",
      "Korta filmer där en god producent räcker",
      "Projekt utan tid för treatment och castingrundor",
    ],
    deliverables: [
      "Treatment och färdigt produktions­manus",
      "Lookbook, shotlist och regibrief",
      "Regi on-set och i postproduktion",
    ],
    priceFrom: {
      amount: "Från 75 000 kr",
      unit: "/ uppdrag",
      note: "Indikativt för treatment + regi-paket. Kan köpas separat eller som del av Prime.",
    },
    process: [
      { n: "01", title: "Briefing", body: "Vi förstår produkten, publiken och vad filmen ska åstadkomma." },
      { n: "02", title: "Treatment", body: "Skrivet dokument med vinkel, referenser, ton och visuell intention." },
      { n: "03", title: "Casting & lookbook", body: "Talang, skådespelare, miljöer och färgvärld — innan kameran rullar." },
      { n: "04", title: "Table-read & rehearsal", body: "Vi går igenom scenerna med talang och team så ingen gissar på set." },
      { n: "05", title: "Regi on-set & i post", body: "Regissören är med från första tagning till sista grade." },
    ],
    faq: [
      {
        q: "Kan vi anlita er bara för manus?",
        a: "Ja. Många kommer till oss för treatment och manus och tar produktionen själva eller via en annan partner.",
      },
      {
        q: "Hur många regiomgångar ingår?",
        a: "Vi jobbar i fasta omgångar: utkast, revidering och slutgodkänt. Extra omgångar är alltid möjliga mot tilläggsoffert.",
      },
      {
        q: "Kan ni regissera på engelska?",
        a: "Ja. Regi och treatment på svenska eller engelska — talang och skådespelare väljer vi efter målgrupp.",
      },
    ],
    related: ["reklam-dokumentar", "film-streaming-tv"],
    stills: [
      "/prime-stills/garageportexperten-fenny-5.jpg",
      "/prime-stills/raaaabaatar-fenny-5.jpg",
      "/prime-stills/garageportexperten-fenny-6.jpg",
      "/prime-stills/fenny-reklam-film-2.jpg",
      "/prime-stills/garageportexperten-fenny-7.jpg",
      "/prime-stills/wimeck-fenny-1.jpg",
      "/prime-stills/garageportexperten-fenny-8.jpg",
      "/prime-stills/raaaabaatar-fenny-3.jpg",
      "/prime-stills/garageportexperten-fenny-9.jpg",
    ],
    videos: [
      { videoId: "9iiyNS4yNzA", title: "Case – Maria på Garageportexperten", client: "Garageportexperten" },
      { videoId: "O2zdqLfmM5E", title: "Garageportexperten Hemma", client: "Garageportexperten" },
      { videoId: "WONrMkeJuik", title: "Wimeck Detailing – Allt på en plats", client: "Wimeck" },
    ],
  },
  {
    slug: "post-produktion",
    number: "05",
    shortTitle: "Post-produktion",
    title: "Post-produktion",
    accent: "peach",
    kicker: "Klipp, grade, ljud och VFX som lyfter råmaterialet ett snäpp.",
    intro:
      "Klippning, färggradering, ljuddesign, VFX, animation och grafik. Vi lyfter råmaterial till ny nivå — eller ger befintligt material nytt liv.",
    metaTitle: "Post-produktion: klipp, grading & VFX | Prime — Fenny",
    metaDescription:
      "Klippning, färggradering, ljuddesign, VFX, motion graphics och animation. Vi tar emot externt material eller jobbar i hela kedjan.",
    hero: { aspect: "wide", flavor: "post" },
    signature: {
      title: "Color & finish — kampanjpaket",
      client: "Konfidentiell uppdragsgivare",
      role: "Klipp · Grade · Ljudmix · VFX",
      year: "2026",
      platform: "Bio · OLV · Social",
      videoId: "_b35xC0m1Ko",
    },
    capabilities: [
      {
        title: "Klipp & dramaturgi",
        body: "Story-driven klippning för långformat, reklam och social — med tydliga revideringsrundor.",
        proof: "Premiere · Resolve · DaVinci-collab",
      },
      {
        title: "Färg, ljud & mix",
        body: "Färggradering i DaVinci Resolve, ljuddesign, foley och stereo/5.1-mix enligt broadcast-spec.",
        proof: "Resolve · Pro Tools · R128",
      },
      {
        title: "Motion, VFX & animation",
        body: "2D/3D-grafik, end-tags, lower thirds, compositing och lättare VFX i After Effects och Nuke.",
        proof: "AE · Nuke · Cinema 4D",
      },
    ],
    proofLogos: ["SVT", "TV4", "Volvo", "ICA", "Spotify", "H&M"],
    proofMetrics: [
      { value: "500+", label: "färdiga filmer i posten 2024–2025" },
      { value: "48h", label: "snitt-tid på cutdowns från godkänd hero" },
      { value: "R128", label: "broadcast-mix som standard" },
    ],
    whenItFits: [
      "Ni har filmat själva men behöver lyfta slutprodukten",
      "Större bibliotek av material som ska paketeras om",
      "Snabba leveranser av cutdowns på löpande basis",
    ],
    notForYou: [
      "Snabba mobil-edits i CapCut — det fixar ni själva",
      "Råmaterial utan struktur eller metadata (vi kan hjälpa, men det kostar)",
      "Projekt utan tydlig leveranslista och kanalformat",
    ],
    deliverables: [
      "Färdig film i alla efterfrågade format",
      "Color-graded master + raw exporter",
      "Återanvändbara grafik­mallar (mogrt/AEP)",
    ],
    priceFrom: {
      amount: "Från 45 000 kr",
      unit: "/ paket",
      note: "Indikativt för klipp + grade + mix av hero + 4–6 cutdowns.",
    },
    process: [
      { n: "01", title: "Ingest & sortering", body: "Vi tar emot materialet, organiserar och säkerhetskopierar." },
      { n: "02", title: "Editorial", body: "Klipp i flera versioner — rough, fine cut, lock." },
      { n: "03", title: "Grade & mix", body: "Färggradering i Resolve, ljuddesign och mix enligt spec." },
      { n: "04", title: "VFX & grafik", body: "End-tags, lower thirds, compositing och animerade element." },
      { n: "05", title: "QC & leverans", body: "Vi QC:ar mot plattform, exporterar i alla format och levererar." },
    ],
    faq: [
      {
        q: "Kan vi skicka råmaterial från en annan produktion?",
        a: "Ja, ofta. Vi tar emot från valfri kamera och bygger en projektstruktur som funkar för revideringar.",
      },
      {
        q: "Hur många revideringar ingår?",
        a: "Två stora revideringsomgångar per leverans är standard. Vi är tydliga med vad som triggar en tredje.",
      },
      {
        q: "Levererar ni i alla sociala format?",
        a: "Ja — 1:1, 9:16, 4:5 och 16:9 med plattformsanpassad textsäkerhet och loop-punkter.",
      },
    ],
    related: ["reklam-dokumentar", "studio-live-webinars"],
    stills: [
      "/prime-stills/bolt-fenny-4.jpg",
      "/prime-stills/fenny-reklam-film-5.jpg",
      "/prime-stills/recompany-1.jpg",
      "/prime-stills/bolt-fenny-5.jpg",
      "/prime-stills/fenny-reklam-film-6.jpg",
      "/prime-stills/recompany-2.jpg",
      "/prime-stills/fenny-reklam-film-7.jpg",
      "/prime-stills/raaaabaatar-fenny-4.jpg",
    ],
    videos: [
      { videoId: "_b35xC0m1Ko", title: "Årets Pryl 2026 – grafik (den smarta ringen)", client: "Elektronikbranschen" },
      { videoId: "yvojXrb41Aw", title: "Bolt kampanj (20 sek)", client: "Bolt" },
      { videoId: "BzHML38R2bs", title: "Pricka — reklam", client: "Pricka" },
    ],
  },
  {
    slug: "poddar",
    number: "06",
    shortTitle: "Live- & studiopoddar",
    title: "Live- & studiopoddar",
    accent: "butter",
    kicker: "Studiopoddar med tv-finish — och en distribution som faktiskt händer.",
    intro:
      "Poddproduktion med hög teknisk finish och dramaturgi, från radiosittning till filmad studiopodd. Komplett inkl. bilder, omslag, publicering och klippning.",
    metaTitle: "Podcast & studiopodd-produktion | Prime — Fenny",
    metaDescription:
      "Vi producerar filmade studiopoddar och rena ljudpoddar — från koncept och format till inspelning, klipp, publicering och marknadsföring.",
    hero: { aspect: "square", flavor: "podcast" },
    signature: {
      title: "Svensk Radonförening — podden",
      client: "Svensk Radonförening",
      role: "Format · Studio · Klipp · Distribution",
      year: "2026",
      platform: "Spotify · Apple · YouTube",
      videoId: "oXoAyTogRrA",
      spotifyShowUrl: "https://open.spotify.com/show/2HSsJDE5oGXLMOh2tadmYM",
    },
    capabilities: [
      {
        title: "Formatutveckling",
        body: "Vinkel, programledare, gästflöde, jingle och visuell identitet som håller över en hel säsong.",
        proof: "Säsongsbibel · jingle · grafik",
      },
      {
        title: "Inspelning",
        body: "Filmad studiopodd med 3–4 kameror eller dedikerad ljudsession i akustisk studio.",
        proof: "Shure · Sound Devices · 3–4 cams",
      },
      {
        title: "Distribution & klipp",
        body: "Publicering till Spotify/Apple/YouTube + 5–10 sociala klipp per avsnitt.",
        proof: "5–10 klipp / avsnitt · RSS-hosting",
      },
    ],
    proofLogos: ["Acast", "Spotify", "Apple", "Podimo", "Bauer", "Sveriges Radio"],
    proofMetrics: [
      { value: "+62%", label: "snitt-tillväxt på säsong 2 vs säsong 1" },
      { value: "8 ep", label: "typisk säsong med fast leveranstakt" },
      { value: "10x", label: "sociala klipp per inspelat avsnitt" },
    ],
    whenItFits: [
      "Företag som vill bygga thought-leadership långsiktigt",
      "Befintliga poddar som behöver lyftas tekniskt",
      "B2B-organisationer som vill nå nischade målgrupper",
    ],
    notForYou: [
      "Engångs-avsnitt utan publiceringsplan",
      "Format utan en programledare som faktiskt har tid att medverka",
      "Projekt utan budget för marknadsföring eller distribution",
    ],
    deliverables: [
      "Halv- eller heldag i vår studio",
      "Radiopodd (ljud) eller videopodd med 3 kameravinklar",
      "Klippning, mastering och uppladdning på poddkanal + YouTube",
      "Omslagsbilder, behind the scenes och bilder för sociala medier",
      "Skapa så många avsnitt eller klipp ni hinner under dagen — fast pris",
    ],
    priceFrom: {
      amount: "Från 16 900 kr",
      unit: "/ halvdag",
      note: "Fast pris per studiodag — ni gör så många avsnitt eller klipp ni hinner med. Inga avgifter per avsnitt.",
    },
    pricingPackages: [
      {
        name: "Radiopodd · halvdag",
        price: "16 900 kr",
        unit: "/ halvdag i studio",
        bullets: [
          "Rent ljud, ingen video",
          "Klipp, mastering & uppladdning",
          "Omslag + bilder för sociala medier",
          "Så många avsnitt ni hinner",
        ],
      },
      {
        name: "Radiopodd · heldag",
        price: "22 000 kr",
        unit: "/ heldag i studio",
        bullets: [
          "Rent ljud, ingen video",
          "Klipp, mastering & uppladdning",
          "Omslag + bilder för sociala medier",
          "Så många avsnitt ni hinner",
        ],
      },
      {
        name: "Videopodd · halvdag",
        price: "22 000 kr",
        unit: "/ halvdag i studio",
        bullets: [
          "3 kameravinklar + ljud",
          "Klipp, mastering & uppladdning på pod + YouTube",
          "Omslag, BTS & sociala bilder",
          "Så många avsnitt eller klipp ni hinner",
        ],
      },
      {
        name: "Videopodd · heldag",
        price: "28 000 kr",
        unit: "/ heldag i studio",
        bullets: [
          "3 kameravinklar + ljud",
          "Klipp, mastering & uppladdning på pod + YouTube",
          "Omslag, BTS & sociala bilder",
          "Så många avsnitt eller klipp ni hinner",
        ],
      },
    ],
    process: [
      { n: "01", title: "Format & säsongsbibel", body: "Vinkel, programledare, gäst-lista och visuell identitet." },
      { n: "02", title: "Studiouppsättning", body: "3–4 kameror, akustiskt rum, mickar och ljus — alltid samma rigg." },
      { n: "03", title: "Inspelning", body: "Halv- eller heldag i studion — gör så många avsnitt ni hinner." },
      { n: "04", title: "Klipp & klipp-klipp", body: "Avsnitt + sociala klipp med textade hooks ingår." },
      { n: "05", title: "Distribution", body: "Publicering till Spotify, Apple, YouTube och er egen kanal." },
    ],
    faq: [
      {
        q: "Behöver vi en egen studio?",
        a: "Nej — vi har en färdig studio. Vill ni bygga er egen så hjälper vi gärna till med ritning och teknikval.",
      },
      {
        q: "Hjälper ni till att hitta gäster?",
        a: "Ja. Vi har ett brett nätverk och kan bidra med både gäst-research och bokningsstöd.",
      },
      {
        q: "Hur ser ett typiskt poddsamarbete ut?",
        a: "En säsong på 8 avsnitt, inspelade i 2 block, med löpande publicering och sociala klipp i 12–16 veckor.",
      },
    ],
    related: ["studio-live-webinars", "post-produktion"],
    stills: [
      "/prime-stills/svensk-radonfoorening-1.jpg",
      "/prime-stills/also-fenny-1.jpg",
      "/prime-stills/pricka-fenny-1.jpg",
      "/prime-stills/elektronikbranschen-fenny-2.jpg",
      "/prime-stills/also-fenny-3.jpg",
      "/prime-stills/pricka-fenny-2.jpg",
    ],
    videos: [
      { videoId: "oXoAyTogRrA", title: "Svensk Radonförening — podden", client: "Svensk Radonförening" },
    ],
  },
  {
    slug: "klipp-grade",
    number: "07",
    shortTitle: "Klippning & gradering",
    title: "Klippning & gradering",
    accent: "peach",
    kicker: "Editor- och colorist-team som lyfter er film — fristående eller whitelabel.",
    intro:
      "Editor och colorist på dagsbasis. Vi tar emot råmaterial från valfri kamera och levererar under er logga eller vår. Återkommande post-partner åt andra produktionsbolag, byråer och in-house team — eller en fristående ingång när ni bara behöver klipp eller grade.",
    metaTitle: "Klippning & gradering på dagsbasis | Fenny",
    metaDescription:
      "Hyr editor eller colorist på dagsbasis. Klipp i Premiere/Resolve, color grading i DaVinci Resolve. Whitelabel-leverans till produktionsbolag och byråer.",
    hero: { aspect: "wide", flavor: "post" },
    signature: {
      title: "Color & finish — whitelabel-paket",
      client: "Konfidentiell uppdragsgivare",
      role: "Klipp · Grade · Leverans under kundlogga",
      year: "2026",
      platform: "OLV · Social · Broadcast",
      videoId: "_b35xC0m1Ko",
    },
    capabilities: [
      {
        title: "Editorial",
        body: "Story-driven klipp för reklam, dokumentär och social. Rough cut, fine cut, lock — med tydliga revideringsrundor.",
        proof: "Premiere · Resolve · 2 rev-omgångar",
      },
      {
        title: "Color grading",
        body: "Färggradering i DaVinci Resolve. Look development, hudtonsmatch, LUT-paket och broadcast-finish.",
        proof: "DaVinci Resolve · ACES · LUT-paket",
      },
      {
        title: "Whitelabel & konsult",
        body: "Vi jobbar under er logga eller utan logga alls. NDA, leverans direkt i er pipeline och projektfiler om ni vill ta över själva.",
        proof: "NDA · projektfiler · er pipeline",
      },
    ],
    proofLogos: ["SVT", "TV4", "Volvo", "Spotify", "ICA", "H&M"],
    proofMetrics: [
      { value: "500+", label: "filmer i posten 2024–2025" },
      { value: "48h", label: "snitt-tid för cutdowns från godkänd hero" },
      { value: "Resolve", label: "certifierad colorist i fast roster" },
    ],
    whenItFits: [
      "Andra produktionsbolag eller byråer som outsourcar post löpande",
      "Egen råfilm som behöver lyftas av en erfaren editor eller colorist",
      "Återkommande grade-partner under en kampanj eller säsong",
    ],
    notForYou: [
      "Ni behöver fullt post-paket inkl. ljud, VFX och motion (välj Post-produktion)",
      "Snabba mobil-edits i CapCut — det fixar ni själva",
      "Råmaterial helt utan struktur och utan tid för organisering",
    ],
    deliverables: [
      "Klippt master + alla efterfrågade versioner",
      "Color-graded master + LUT-paket för fortsatt arbete",
      "Projektfiler (Premiere/Resolve) på begäran för handover",
    ],
    priceFrom: {
      amount: "Från 9 500 kr",
      unit: "/ dag",
      note: "Indikativt för editor heldag. Whitelabel-uppdrag och längre engagemang prissätts per projekt.",
    },
    pricingPackages: [
      {
        name: "Editor · halvdag",
        price: "5 500 kr",
        unit: "/ halvdag",
        bullets: [
          "Premiere eller Resolve",
          "1 revideringsrunda ingår",
          "Whitelabel-leverans möjlig",
          "Projektfiler på begäran",
        ],
      },
      {
        name: "Editor · heldag",
        price: "9 500 kr",
        unit: "/ heldag",
        bullets: [
          "Premiere eller Resolve",
          "2 revideringsrundor ingår",
          "Whitelabel-leverans möjlig",
          "Projektfiler + cutdowns",
        ],
      },
      {
        name: "Colorist · halvdag",
        price: "6 500 kr",
        unit: "/ halvdag",
        bullets: [
          "DaVinci Resolve",
          "Look development + grade",
          "LUT-paket levereras",
          "Whitelabel-leverans möjlig",
        ],
      },
      {
        name: "Colorist · heldag",
        price: "11 500 kr",
        unit: "/ heldag",
        bullets: [
          "DaVinci Resolve",
          "Hero + cutdowns i samma look",
          "LUT-paket + projektfiler",
          "Broadcast-finish (R128 vid behov)",
        ],
      },
    ],
    process: [
      { n: "01", title: "Ingest & sortering", body: "Vi tar emot råmaterial från valfri kamera och bygger en projektstruktur som funkar för revideringar." },
      { n: "02", title: "Editorial", body: "Rough cut, fine cut, lock. Tydliga revideringsrundor och feedback i er kanal." },
      { n: "03", title: "Color grading", body: "Look development, hudtonsmatch, hero + cutdowns i samma look. Levereras med LUT-paket." },
      { n: "04", title: "Revideringar", body: "Två omgångar ingår i heldagspaketen. Extra omgångar mot tillägg, alltid med tydlig offert." },
      { n: "05", title: "Leverans & handover", body: "Master i alla format, projektfiler om ni vill ta över själva, leverans under er logga vid whitelabel." },
    ],
    faq: [
      {
        q: "Kan ni jobba whitelabel under vår logga?",
        a: "Ja. Vi kör NDA, levererar direkt i er pipeline och dyker aldrig upp mot er slutkund. Många av våra dagar går till andra produktionsbolag och byråer.",
      },
      {
        q: "Vilka format tar ni emot?",
        a: "Allt från ARRI, RED, Sony Venice och FX-serien till BMPCC och DSLR. Skicka via WeTransfer, Frame.io, Dropbox eller fysisk disk — vi sköter ingest.",
      },
      {
        q: "Hur många revideringsrundor ingår?",
        a: "Två stora omgångar per heldagspaket. Vi är tydliga upfront med vad som triggar en tredje omgång så ni aldrig blir överraskade på fakturan.",
      },
    ],
    related: ["post-produktion", "reklam-dokumentar"],
    stills: [
      "/prime-stills/bolt-fenny-4.jpg",
      "/prime-stills/fenny-reklam-film-5.jpg",
      "/prime-stills/recompany-1.jpg",
      "/prime-stills/bolt-fenny-5.jpg",
      "/prime-stills/fenny-reklam-film-6.jpg",
      "/prime-stills/recompany-2.jpg",
      "/prime-stills/fenny-reklam-film-7.jpg",
    ],
    videos: [
      { videoId: "_b35xC0m1Ko", title: "Årets Pryl 2026 – grafik (den smarta ringen)", client: "Elektronikbranschen" },
      { videoId: "yvojXrb41Aw", title: "Bolt kampanj (20 sek)", client: "Bolt" },
    ],
  },
  {
    slug: "ljussattning",
    number: "08",
    shortTitle: "Ljussättning & gaffer",
    title: "Ljussättning & gaffer-team",
    accent: "butter",
    kicker: "Gaffer, ljusdesigner och paket — för era inspelningar eller våra.",
    intro:
      "Vi hyr ut gaffer, best boy och spark med komplett ljuspaket till andra produktionsbolag, byråer och företag — eller designar ljuset i våra egna Prime-produktioner. Cinematografisk ljussättning, inte bara en softbox.",
    metaTitle: "Ljussättning & gaffer på dagsbasis | Fenny",
    metaDescription:
      "Hyr gaffer och ljusteam för filminspelning, med eller utan ljuspaket. Ljussättning för byråer, produktionsbolag och Fenny Prime.",
    hero: { aspect: "wide", flavor: "cinema" },
    signature: {
      title: "Cinematografisk ljusdesign — kampanj",
      client: "Konfidentiell uppdragsgivare",
      role: "Gaffer · Ljusdesign · Rigg",
      year: "2026",
      platform: "Bio · OLV · Social",
      videoId: "O2zdqLfmM5E",
    },
    capabilities: [
      {
        title: "Gaffer & ljusteam",
        body: "Erfaren gaffer, best boy och spark på dagsbasis. Vi kommer in på era inspelningar med eller utan eget ljuspaket.",
        proof: "Gaffer i fast roster · IATSE-rutin",
      },
      {
        title: "Ljusdesign för film",
        body: "Cinematografisk ljussättning i Prime-produktioner. Look development tillsammans med DP och regissör, från lookbook till set.",
        proof: "Lookbook · ljusplan · referensgrade",
      },
      {
        title: "Ljuspaket & rigg",
        body: "Aputure, ARRI och Astera-paket inkl. grip, stativ, flaggor och färgfilter. Riggdag i studio, location eller OB-buss.",
        proof: "ARRI · Aputure · Astera · grip",
      },
    ],
    proofLogos: ["SVT", "TV4", "Volvo", "ICA", "Polestar", "H&M"],
    proofMetrics: [
      { value: "150+", label: "inspelningsdagar med ljusteam per år" },
      { value: "ARRI", label: "+ Aputure + Astera i fast paket" },
      { value: "Gaffer", label: "i fast roster + 3 best boys" },
    ],
    whenItFits: [
      "Produktionsbolag eller byråer som hyr in komplett ljusteam för shoot-dagar",
      "Företag med egen inspelning som behöver pro-ljus istället för softbox-stativ",
      "Prime-produktioner där look:en kräver dedikerad gaffer från dag ett",
    ],
    notForYou: [
      "Ett softbox-stativ till en kort intervju — vi rör oss i den större ändan",
      "Inspelningar utan någon på er sida som regisserar ljuset (vi gaffer:ar, ni leder bilden)",
      "Budget utan utrymme för team + paket + transport",
    ],
    deliverables: [
      "Gaffer + team enligt brief (gaffer, best boy, spark)",
      "Komplett ljuspaket inkl. transport till location",
      "Riggdag innan inspelning vid större setups",
    ],
    priceFrom: {
      amount: "Från 6 900 kr",
      unit: "/ dag",
      note: "Indikativt för gaffer solo. Team och paket prissätts efter brief och resor utanför Skåne/Stockholm.",
    },
    pricingPackages: [
      {
        name: "Gaffer solo · dag",
        price: "6 900 kr",
        unit: "/ dag",
        bullets: [
          "Bara gaffern — ni har eget paket",
          "Förmöte och recce ingår",
          "Upp till 10 timmar på set",
          "Resor utanför Skåne tillkommer",
        ],
      },
      {
        name: "Gaffer + ljusteam · dag",
        price: "14 500 kr",
        unit: "/ dag",
        bullets: [
          "Gaffer + best boy + spark",
          "Ert paket eller vi tar med",
          "Upp till 10 timmar på set",
          "Riggdag bokas separat",
        ],
      },
      {
        name: "Ljuspaket S · dag",
        price: "4 500 kr",
        unit: "/ dag",
        bullets: [
          "Aputure 4-pack + grip",
          "Stativ, flaggor, färgfilter",
          "Exkl. team — ni har egen ljussättare",
          "Transport inom Skåne ingår",
        ],
      },
      {
        name: "Ljuspaket L + team · dag",
        price: "22 000 kr",
        unit: "/ dag",
        bullets: [
          "ARRI + Astera + Aputure-rigg",
          "Gaffer + best boy + spark",
          "Komplett grip- och kabel-paket",
          "Transport och rigg ingår",
        ],
      },
    ],
    process: [
      { n: "01", title: "Brief & recce", body: "Vi går igenom shotlist, lookbook och location. Gaffern är med från första möte." },
      { n: "02", title: "Ljusdesign & plan", body: "Ljusplan, paket-lista, strömbudget och riggschema. Inga överraskningar på set." },
      { n: "03", title: "Riggdag", body: "Vid större setups bygger vi dagen innan så ni kan rulla direkt på inspelningsdag ett." },
      { n: "04", title: "Inspelning", body: "Gaffer leder ljusteamet, samspelar med DP och regi. Vi håller tempot uppe." },
      { n: "05", title: "Wrap", body: "Vi packar ner, lämnar location som vi hittade den och hanterar all retur av paket." },
    ],
    faq: [
      {
        q: "Reser ni utanför Skåne och Stockholm?",
        a: "Ja. Vi bokar regelbundet team och paket över hela Sverige och i Norden. Resor och logi prissätts transparent i offert.",
      },
      {
        q: "Kan vi bara hyra paketet utan team?",
        a: "Ja, om ni har en egen ljussättare eller gaffer som kan ta ansvar. Vi går igenom paketet vid uthämtning och säkrar att allt funkar.",
      },
      {
        q: "Hur funkar det med försäkring och ansvar?",
        a: "Allt vårt paket är försäkrat. När ni hyr utan team tecknar vi en kort hyresöverenskommelse som täcker ansvar, skador och försenad återlämning.",
      },
    ],
    related: ["reklam-dokumentar", "film-streaming-tv"],
    stills: [
      "/prime-stills/garageportexperten-fenny-1.jpg",
      "/prime-stills/bolt-fenny-1.jpg",
      "/prime-stills/fenny-reklam-film-1.jpg",
      "/prime-stills/garageportexperten-fenny-3.jpg",
      "/prime-stills/bolt-fenny-2.jpg",
      "/prime-stills/wimeck-fenny-1.jpg",
      "/prime-stills/garageportexperten-fenny-5.jpg",
      "/prime-stills/fenny-reklam-film-3.jpg",
    ],
    videos: [
      { videoId: "O2zdqLfmM5E", title: "Garageportexperten Hemma", client: "Garageportexperten" },
      { videoId: "WONrMkeJuik", title: "Wimeck Detailing – Allt på en plats", client: "Wimeck" },
    ],
  },
];

export const getPrimeService = (slug: string) =>
  primeServices.find((s) => s.slug === slug);
